import { BenchmarkGroupType } from '@app/src/types/benchmark'
import type { Provider } from '@app/src/types/organizations'
import { MenuItem, Select, Skeleton, Stack, Typography } from '@mui/material'
import React from 'react'
import type { IntlShape } from 'react-intl'
import { useIntl } from 'react-intl'
import { getAvailablePeerGroups, peerGroupOptionMessageId, revenueRangeGroupValue } from '../benchmark/peerGroups'

interface BenchmarkCompareFilterProps {
  provider: Provider
  peerGroup: BenchmarkGroupType
  peerCount?: number
  isLoading: boolean
  onPeerGroupChange: (peerGroup: BenchmarkGroupType) => void
}

const LABEL_ID = 'benchmark-compare-to-label'

function peerGroupValue(
  provider: Provider,
  group: BenchmarkGroupType,
  formatMessage: IntlShape['formatMessage'],
): string | undefined {
  const revenueRange = revenueRangeGroupValue(provider)
  switch (group) {
    case BenchmarkGroupType.Industry:
      return provider.industry
        ? formatMessage(
            { id: 'sustainabilityProfile.benchmark.industryValue' },
            { industry: provider.industry.description, level: provider.industry.level },
          )
        : undefined
    case BenchmarkGroupType.Region:
      return provider.country?.region?.name
    case BenchmarkGroupType.RevenueRange:
      return revenueRange ? formatMessage({ id: `claim.companyRevenue.${revenueRange}` }) : undefined
    default:
      return undefined
  }
}

const BenchmarkCompareFilter: React.FC<BenchmarkCompareFilterProps> = ({
  provider,
  peerGroup,
  peerCount,
  isLoading,
  onPeerGroupChange,
}) => {
  const { formatMessage } = useIntl()
  const value = peerGroupValue(provider, peerGroup, formatMessage)

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="wrap"
      columnGap={1}
      p={1}
      border={1}
      borderColor="divider"
      borderRadius={2}
      bgcolor="background.paper"
    >
      <Stack direction="row" alignItems="center" spacing={1} pl={1}>
        <Typography id={LABEL_ID} variant="button" component="span">
          {formatMessage({ id: 'sustainabilityProfile.benchmark.compareTo' })}
        </Typography>
        <Select
          variant="standard"
          disableUnderline
          value={peerGroup}
          onChange={event => onPeerGroupChange(event.target.value as BenchmarkGroupType)}
          inputProps={{ 'aria-labelledby': LABEL_ID }}
          renderValue={group => (
            <Typography
              component="span"
              variant="body2"
              fontWeight="medium"
              px={1}
              py={0.5}
              bgcolor="action.selected"
              borderRadius={1}
            >
              {formatMessage({ id: peerGroupOptionMessageId(group) })}
            </Typography>
          )}
          sx={{ '& .MuiSelect-select:focus': { bgcolor: 'transparent' } }}
        >
          {getAvailablePeerGroups(provider).map(group => (
            <MenuItem key={group} value={group}>
              {formatMessage({ id: peerGroupOptionMessageId(group) })}
            </MenuItem>
          ))}
        </Select>
      </Stack>

      {isLoading ? (
        <Skeleton variant="text" width={320} />
      ) : (
        <Typography variant="body1">
          {value && (
            <Typography component="span" variant="subtitle1">
              {`${value} `}
            </Typography>
          )}
          {peerCount !== undefined
            ? formatMessage({ id: 'sustainabilityProfile.benchmark.peerCount' }, { count: peerCount })
            : formatMessage({ id: 'sustainabilityProfile.benchmark.unavailable' })}
        </Typography>
      )}
    </Stack>
  )
}

export default BenchmarkCompareFilter
